package top.wcpe.allincore.ui.runtime.ir.state

import top.wcpe.allincore.dsl.ir.expr.IrType
import top.wcpe.allincore.ui.runtime.internal.concurrent.ConcurrentMutableMap
import top.wcpe.allincore.ui.runtime.internal.log.UiLog
import top.wcpe.allincore.ui.runtime.reactive.Reactive
import top.wcpe.allincore.ui.runtime.reactive.ReactiveWrapper
import top.wcpe.allincore.ui.runtime.reactive.Signal
import kotlin.concurrent.Volatile
import kotlin.jvm.JvmStatic

/** session / persist 表达式访问接缝；默认实现仍为 [SharedStateRegistry]。 */
interface SharedStateAccess {
    fun sessionSignal(fieldName: String, defaultValue: Any?): Signal<Any?>

    fun persistSignal(fieldName: String, defaultValue: Any?): Signal<Any?>

    fun declarePersistField(
        fieldName: String,
        defaultValue: Any?,
        expectedType: IrType?,
        warningSink: ((String) -> Unit)? = null,
    ): Signal<Any?>

    fun clearSession()
}

/** 让 session 装配入口从既有注入对象取得共享状态域，未实现时仍回退全局注册表。 */
interface SharedStateAccessProvider {
    val sharedStateAccess: SharedStateAccess
}

/**
 * 单个应用的候选共享状态域：session 每个候选独立，persist 从最近一次已激活候选复制。
 * 候选只有调用 [activate] 后才成为后续候选的 persist 基线，失败候选直接丢弃即可。
 */
class SharedStateScope private constructor(
    private val application: ApplicationState,
    private val persistState: PersistState = application.persistState,
) : SharedStateAccess {
    private val sessionSignals = ConcurrentMutableMap<String, Signal<Any?>>()

    constructor() : this(ApplicationState())

    fun newCandidateScope(): SharedStateScope = SharedStateScope(application, application.persistState.copy())

    fun activate() {
        application.persistState = persistState
    }

    override fun sessionSignal(fieldName: String, defaultValue: Any?): Signal<Any?> =
        sessionSignals.computeIfAbsent(fieldName) { Reactive.signal(ReactiveWrapper.wrap(defaultValue)) }

    override fun persistSignal(fieldName: String, defaultValue: Any?): Signal<Any?> =
        persistState.signals.computeIfAbsent(fieldName) { Reactive.signal(ReactiveWrapper.wrap(defaultValue)) }

    override fun declarePersistField(
        fieldName: String,
        defaultValue: Any?,
        expectedType: IrType?,
        warningSink: ((String) -> Unit)?,
    ): Signal<Any?> {
        val declaration = PersistDeclaration(defaultValue, expectedType)
        persistState.declarations[fieldName] = declaration
        val signal = persistSignal(fieldName, defaultValue)
        val current = signal.get()
        if (!matchesSharedStateType(expectedType, current)) {
            signal.set(ReactiveWrapper.wrap(defaultValue))
            emitTypeMismatch(fieldName, current, declaration, warningSink)
        }
        return signal
    }

    override fun clearSession() {
        sessionSignals.clear()
    }

    private fun emitTypeMismatch(
        fieldName: String,
        actualValue: Any?,
        declaration: PersistDeclaration,
        warningSink: ((String) -> Unit)?,
    ) {
        val message = persistTypeMismatchMessage(fieldName, actualValue, declaration.expectedType)
        if (warningSink == null) LOGGER.warn(message) else warningSink(message)
    }

    private class ApplicationState(@Volatile var persistState: PersistState = PersistState())

    private class PersistState(
        val signals: ConcurrentMutableMap<String, Signal<Any?>> = ConcurrentMutableMap(),
        val declarations: ConcurrentMutableMap<String, PersistDeclaration> = ConcurrentMutableMap(),
    ) {
        fun copy(): PersistState {
            val copied = PersistState()
            declarations.entriesSnapshot().forEach { (name, declaration) ->
                copied.declarations[name] = declaration
            }
            signals.entriesSnapshot().forEach { (name, signal) ->
                val snapshot = snapshotSharedStateValue(signal.get())
                copied.signals[name] = Reactive.signal(ReactiveWrapper.wrap(snapshot))
            }
            return copied
        }
    }

    private data class PersistDeclaration(val defaultValue: Any?, val expectedType: IrType?)

    private companion object {
        val LOGGER = UiLog("top.wcpe.allincore.ui.runtime.ir.state.SharedStateScope")
    }
}

private fun snapshotSharedStateValue(value: Any?): Any? = when (value) {
    is Map<*, *> -> value.entries.associate { (key, item) -> key.toString() to snapshotSharedStateValue(item) }
    is Iterable<*> -> value.map(::snapshotSharedStateValue)
    is Array<*> -> value.map(::snapshotSharedStateValue)
    else -> value
}

/**
 * 跨 screen / hud 共享的 state 字段注册表。
 *
 * session state 按「玩家 + 服务器 + namespace + 字段」隔离：同一作用域内所有 IR 实例共享同一个 Signal，
 * 切换玩家 / 服务器或断开连接时清空 session 内存状态。
 *
 * 线程安全：用 [ConcurrentMutableMap] 持有作用域 key → Signal 映射；Signal 实例本身的并发由 reactive
 * 内部保证。
 *
 * persist state 同样按「玩家 + 服务器 + namespace + 字段」隔离，但切换作用域不清空旧作用域内存；
 * 落盘 / 加载由 client-shared 的 PersistentStateStore 负责。
 */
object SharedStateRegistry : SharedStateAccess {

    private val logger = UiLog("top.wcpe.allincore.ui.runtime.ir.state.SharedStateRegistry")

    private const val LEGACY_SCOPE = "_legacy"
    private const val WARN_PERSIST_TYPE_MISMATCH = "AUI-V2-201"

    @Volatile
    private var activeScope = Scope(LEGACY_SCOPE, LEGACY_SCOPE)

    private val sessionSignals = ConcurrentMutableMap<StateKey, Signal<Any?>>()
    private val persistSignals = ConcurrentMutableMap<StateKey, Signal<Any?>>()
    private val persistDeclarations = ConcurrentMutableMap<StateKey, PersistDeclaration>()

    /** 全局唯一实例：保留 `SharedStateRegistry.global()` 的静态访问 ABI。 */
    @JvmStatic
    fun global(): SharedStateRegistry = this

    /**
     * 激活当前玩家 / 服务器作用域。作用域变化时清空 session 内存状态，避免切服或切号后读到旧玩家数据。
     */
    fun activateScope(serverKey: String?, playerKey: String?) {
        val next = Scope(normalizeScopePart(serverKey), normalizeScopePart(playerKey))
        val previous = activeScope
        if (previous == next) return
        sessionSignals.clear()
        activeScope = next
    }

    /** 断开服务器或退出客户端时清空 session，并回到 legacy 兜底作用域。 */
    fun disconnectScope() {
        sessionSignals.clear()
        persistDeclarations.clear()
        activeScope = Scope(LEGACY_SCOPE, LEGACY_SCOPE)
    }

    /** 清空当前 session 作用域内的字段。 */
    override fun clearSession() {
        sessionSignals.clear()
    }

    /**
     * 取 / 建一个 session 字段对应的 Signal。
     *
     * 多次以同一作用域 + fieldName 调用返回同一实例；首次调用使用传入的 [defaultValue] 初始化，后续调用忽略
     * default（已有值优先）。默认值若是 List/Map 会被 [ReactiveWrapper.wrap] 递归包成反应式集合，
     * 与 `state.*` 行为一致。
     */
    override fun sessionSignal(fieldName: String, defaultValue: Any?): Signal<Any?> =
        sessionSignals.computeIfAbsent(keyOf(fieldName)) { Reactive.signal(ReactiveWrapper.wrap(defaultValue)) }

    /**
     * 取 / 建一个 persist 字段对应的 Signal。语义与 [sessionSignal] 一致，但按 active scope 独立保存。
     */
    override fun persistSignal(fieldName: String, defaultValue: Any?): Signal<Any?> =
        persistSignals.computeIfAbsent(keyOf(fieldName)) { Reactive.signal(ReactiveWrapper.wrap(defaultValue)) }

    /** 从磁盘加载当前作用域的 persist 字段值；已声明字段会立即执行字段级类型校验。 */
    fun loadPersistFields(fields: Map<String, Any?>, warningSink: ((String) -> Unit)? = null) {
        for ((fieldName, value) in fields) {
            val key = keyOf(fieldName)
            val declaration = persistDeclarations[key]
            val next = validatedPersistValue(fieldName, value, declaration, warningSink)
            persistSignals.computeIfAbsent(key) { Reactive.signal(ReactiveWrapper.wrap(next)) }
                .set(ReactiveWrapper.wrap(next))
        }
    }

    /**
     * 声明一个 persist 字段及默认值。若当前已有落盘值但类型与 DSL 声明不符，回退默认值并输出 warning。
     */
    override fun declarePersistField(
        fieldName: String,
        defaultValue: Any?,
        expectedType: IrType?,
        warningSink: ((String) -> Unit)?,
    ): Signal<Any?> {
        val key = keyOf(fieldName)
        val declaration = PersistDeclaration(defaultValue, expectedType)
        persistDeclarations[key] = declaration
        val signal = persistSignals.computeIfAbsent(key) { Reactive.signal(ReactiveWrapper.wrap(defaultValue)) }
        val current = signal.get()
        if (!matchesType(declaration.expectedType, current)) {
            signal.set(ReactiveWrapper.wrap(defaultValue))
            emitPersistTypeMismatch(fieldName, current, declaration, warningSink)
        }
        return signal
    }

    /** 复制当前 session 字段表（仅供测试 / 诊断用）。 */
    fun snapshotSession(): Map<String, Signal<Any?>> = snapshot(sessionSignals)

    /** 复制当前作用域的 persist 字段表，供 PersistentStateStore 在 flush 时枚举字段值。 */
    fun snapshotPersist(): Map<String, Signal<Any?>> = snapshot(persistSignals)

    /** 测试用：清空两份注册表，并重置当前作用域。 */
    fun clearForTest() {
        sessionSignals.clear()
        persistSignals.clear()
        persistDeclarations.clear()
        activeScope = Scope(LEGACY_SCOPE, LEGACY_SCOPE)
    }

    private fun validatedPersistValue(
        fieldName: String,
        value: Any?,
        declaration: PersistDeclaration?,
        warningSink: ((String) -> Unit)?,
    ): Any? {
        if (declaration == null || matchesType(declaration.expectedType, value)) {
            return value
        }
        emitPersistTypeMismatch(fieldName, value, declaration, warningSink)
        return declaration.defaultValue
    }

    private fun emitPersistTypeMismatch(
        fieldName: String,
        actualValue: Any?,
        declaration: PersistDeclaration,
        warningSink: ((String) -> Unit)?,
    ) {
        val message = "$WARN_PERSIST_TYPE_MISMATCH persist 字段类型不匹配，已回退默认值 field=$fieldName " +
            "expected=${displayName(declaration.expectedType)} actual=${actualName(actualValue)}"
        if (warningSink == null) {
            logger.warn(message)
        } else {
            warningSink(message)
        }
    }

    private fun snapshot(source: ConcurrentMutableMap<StateKey, Signal<Any?>>): Map<String, Signal<Any?>> {
        val scope = activeScope
        val out = LinkedHashMap<String, Signal<Any?>>()
        for ((key, value) in source.entriesSnapshot()) {
            if (key.scope == scope) {
                out[key.logicalName()] = value
            }
        }
        return out
    }

    private fun keyOf(fieldName: String): StateKey {
        val field = FieldName.parse(fieldName)
        return StateKey(activeScope, field.namespace, field.name)
    }

    private fun normalizeScopePart(value: String?): String {
        val trimmed = value?.trim()
        return if (trimmed.isNullOrEmpty()) LEGACY_SCOPE else trimmed
    }

    private fun matchesType(expectedType: IrType?, value: Any?): Boolean {
        if (expectedType == null || expectedType == IrType.ANY || expectedType == IrType.NULLABLE_ANY) {
            return true
        }
        if (value == null) {
            return expectedType.isNullable()
        }
        return when (expectedType) {
            IrType.NUMBER, IrType.NULLABLE_NUMBER -> value is Number
            IrType.STRING, IrType.NULLABLE_STRING -> value is String
            IrType.BOOLEAN, IrType.NULLABLE_BOOLEAN -> value is Boolean
            IrType.MAP, IrType.NULLABLE_MAP -> value is Map<*, *>
            IrType.LIST, IrType.NULLABLE_LIST -> value is List<*>
            IrType.ANY, IrType.NULLABLE_ANY -> true
        }
    }

    private fun displayName(type: IrType?): String = when (type) {
        IrType.NUMBER -> "Number"
        IrType.STRING -> "String"
        IrType.BOOLEAN -> "Boolean"
        IrType.MAP -> "Map"
        IrType.LIST -> "List"
        IrType.NULLABLE_NUMBER -> "Number?"
        IrType.NULLABLE_STRING -> "String?"
        IrType.NULLABLE_BOOLEAN -> "Boolean?"
        IrType.NULLABLE_MAP -> "Map?"
        IrType.NULLABLE_LIST -> "List?"
        IrType.NULLABLE_ANY -> "Any?"
        else -> "Any"
    }

    private fun actualName(value: Any?): String = value?.let { it::class.simpleName } ?: "null"

    private data class Scope(val serverKey: String, val playerKey: String)

    private data class PersistDeclaration(val defaultValue: Any?, val expectedType: IrType?)

    private data class StateKey(val scope: Scope, val namespace: String, val fieldName: String) {
        fun logicalName(): String = if (namespace == LEGACY_SCOPE) fieldName else "$namespace.$fieldName"
    }

    private data class FieldName(val namespace: String, val name: String) {
        companion object {
            fun parse(raw: String): FieldName {
                val dot = raw.indexOf('.')
                if (dot <= 0 || dot >= raw.length - 1) {
                    return FieldName(LEGACY_SCOPE, raw)
                }
                return FieldName(raw.substring(0, dot), raw.substring(dot + 1))
            }
        }
    }
}

private fun matchesSharedStateType(expectedType: IrType?, value: Any?): Boolean {
    if (expectedType == null || expectedType == IrType.ANY || expectedType == IrType.NULLABLE_ANY) return true
    if (value == null) return expectedType.isNullable()
    return when (expectedType) {
        IrType.NUMBER, IrType.NULLABLE_NUMBER -> value is Number
        IrType.STRING, IrType.NULLABLE_STRING -> value is String
        IrType.BOOLEAN, IrType.NULLABLE_BOOLEAN -> value is Boolean
        IrType.MAP, IrType.NULLABLE_MAP -> value is Map<*, *>
        IrType.LIST, IrType.NULLABLE_LIST -> value is List<*>
        else -> true
    }
}

private fun persistTypeMismatchMessage(fieldName: String, value: Any?, expectedType: IrType?): String =
    "AUI-V2-201 persist 字段类型不匹配，已回退默认值 field=$fieldName " +
        "expected=${sharedStateTypeName(expectedType)} actual=${value?.let { it::class.simpleName } ?: "null"}"

private fun sharedStateTypeName(type: IrType?): String = when (type) {
    IrType.NUMBER -> "Number"
    IrType.STRING -> "String"
    IrType.BOOLEAN -> "Boolean"
    IrType.MAP -> "Map"
    IrType.LIST -> "List"
    IrType.NULLABLE_NUMBER -> "Number?"
    IrType.NULLABLE_STRING -> "String?"
    IrType.NULLABLE_BOOLEAN -> "Boolean?"
    IrType.NULLABLE_MAP -> "Map?"
    IrType.NULLABLE_LIST -> "List?"
    IrType.NULLABLE_ANY -> "Any?"
    else -> "Any"
}
